import React, { useEffect, useState } from "react";
import { Heart, ImagePlus, Trash2, Upload } from "lucide-react";
import { useDraft } from "../../lib/draft-store";
import {
  absoluteUrl,
  deleteReferenceImage,
  listPortfolioPaginated,
  toggleInspiration,
  uploadReferenceImage,
} from "../../lib/api";
import { track } from "../../lib/track";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { SkeletonImage } from "../shared/Skeleton";

const MAX_UPLOADS = 8;
const ACCEPT_MIMES = "image/jpeg,image/png,image/webp,image/heic,image/heif";

export default function Step4Inspiration() {
  const { id: draftId, payload, flushNow, setField } = useDraft();
  const refs = payload?.references || {};
  const uploaded = refs.images || [];
  const likedIds = refs.liked_portfolio_ids || [];
  const [uploading, setUploading] = useState([]);
  const [uploadError, setUploadError] = useState("");

  const [portfolio, setPortfolio] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 24;

  useEffect(() => {
    let cancelled = false;
    setPageLoading(true);
    (async () => {
      try {
        const data = await listPortfolioPaginated({ limit, offset });
        if (!cancelled) {
          setPortfolio((p) => (offset === 0 ? data.items : [...p, ...data.items]));
          setTotal(data.total || 0);
        }
      } catch {
        /* swallow */
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [offset]);

  const onFiles = async (files) => {
    setUploadError("");
    if (!files || !files.length) return;
    const arr = Array.from(files);
    if (uploaded.length + arr.length > MAX_UPLOADS) {
      setUploadError(`Max ${MAX_UPLOADS} images per draft.`);
      return;
    }
    // Ensure we have a draftId before uploading
    let did = draftId;
    if (!did) {
      const doc = await flushNow();
      did = doc?.id;
    }
    if (!did) {
      setUploadError("Couldn't create draft. Try again in a moment.");
      return;
    }
    for (const f of arr) {
      const tempId = `tmp_${Math.random().toString(36).slice(2)}`;
      setUploading((u) => [...u, { tempId, name: f.name, progress: 0 }]);
      try {
        const res = await uploadReferenceImage(did, f, (p) => {
          setUploading((u) =>
            u.map((it) => (it.tempId === tempId ? { ...it, progress: p } : it)),
          );
        });
        // Append to local payload.references.images
        setField(
          ["references", "images"],
          [...(refs.images || []), { id: res.id, url: res.url }],
        );
        track("quote_reference_uploaded", {
          properties: { mime: res.mime, size_bytes: res.size_bytes },
          draftId: did,
        });
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Upload failed";
        setUploadError(String(msg));
      } finally {
        setUploading((u) => u.filter((it) => it.tempId !== tempId));
      }
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    onFiles(e.dataTransfer.files);
  };

  const removeUploaded = async (img) => {
    if (!draftId) return;
    try {
      await deleteReferenceImage(draftId, img.id);
    } catch {
      /* still scrub local */
    }
    setField(
      ["references", "images"],
      (refs.images || []).filter((i) => i.id !== img.id),
    );
  };

  const toggleLike = async (imgId) => {
    if (!draftId) {
      const doc = await flushNow();
      if (!doc?.id) return;
    }
    try {
      const res = await toggleInspiration(draftId, imgId);
      setField(["references", "liked_portfolio_ids"], res.liked_portfolio_ids || []);
    } catch {
      /* swallow */
    }
  };

  return (
    <div data-testid="plan-step-4">
      <h1 className="font-display text-3xl text-[#1A1A1A] sm:text-4xl">
        Show us your inspiration
      </h1>
      <p className="mt-3 max-w-xl text-base text-[#1A1A1A]/65">
        Optional — upload references or like images from our portfolio. Helps us
        match your mood.
      </p>

      <Tabs defaultValue="upload" className="mt-8">
        <TabsList
          data-testid="inspo-tabs"
          className="grid w-full grid-cols-2 bg-[#EAE3D2]/50"
        >
          <TabsTrigger value="upload" data-testid="inspo-tab-upload">
            Upload references
          </TabsTrigger>
          <TabsTrigger value="browse" data-testid="inspo-tab-browse">
            Browse our portfolio
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="mt-6">
          <div
            data-testid="upload-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#C8A24B]/50 bg-white/60 px-6 py-12 text-center"
          >
            <ImagePlus className="h-7 w-7 text-[#C8A24B]" />
            <p className="mt-3 font-display text-lg text-[#1A1A1A]">
              Drag images here, or
            </p>
            <label
              htmlFor="upload-input"
              className="mt-2 cursor-pointer rounded-full bg-[#1A1A1A] px-5 py-2.5 text-xs uppercase tracking-[0.22em] text-[#FAF7F2] transition hover:bg-[#2a2a2a]"
            >
              <Upload className="mr-2 inline h-3.5 w-3.5" />
              Choose images
            </label>
            <input
              id="upload-input"
              data-testid="upload-input"
              type="file"
              accept={ACCEPT_MIMES}
              multiple
              hidden
              onChange={(e) => onFiles(e.target.files)}
            />
            <p className="mt-3 text-xs text-[#1A1A1A]/55">
              JPEG / PNG / WEBP / HEIC up to 8 MB each. Max {MAX_UPLOADS} images.
            </p>
            {uploadError ? (
              <p data-testid="upload-error" className="mt-3 text-xs text-[#a33]">
                {uploadError}
              </p>
            ) : null}
          </div>

          <p className="mt-6 text-[10px] uppercase tracking-[0.28em] text-[#1A1A1A]/45">
            {uploaded.length} / {MAX_UPLOADS} uploaded
          </p>

          {uploading.length || uploaded.length ? (
            <ul
              data-testid="uploaded-list"
              className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4"
            >
              {uploaded.map((img) => (
                <li
                  key={img.id}
                  data-testid={`uploaded-thumb-${img.id}`}
                  className="group relative aspect-square overflow-hidden rounded-lg border border-[#EAE3D2] bg-white"
                >
                  <img
                    src={absoluteUrl(img.url)}
                    alt="Reference"
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    data-testid={`uploaded-remove-${img.id}`}
                    aria-label="Remove image"
                    onClick={() => removeUploaded(img)}
                    className="absolute right-1.5 top-1.5 rounded-full bg-[#1A1A1A]/70 p-1.5 text-[#FAF7F2] opacity-0 transition group-hover:opacity-100 focus:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
              {uploading.map((u) => (
                <li
                  key={u.tempId}
                  className="relative aspect-square overflow-hidden rounded-lg border border-[#EAE3D2] bg-white"
                >
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#EAE3D2]/40 p-2 text-center">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/60">
                      Uploading
                    </p>
                    <p className="mt-1 text-xs text-[#1A1A1A]">{u.progress}%</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </TabsContent>

        <TabsContent value="browse" className="mt-6">
          <p
            data-testid="inspo-likes-count"
            className="text-[10px] uppercase tracking-[0.28em] text-[#1A1A1A]/45"
          >
            {likedIds.length} {likedIds.length === 1 ? "like" : "likes"}
          </p>
          <ul
            data-testid="inspo-portfolio-grid"
            className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4"
          >
            {pageLoading && portfolio.length === 0
              ? Array.from({ length: 8 }).map((_, i) => (
                  <li key={i}>
                    <SkeletonImage className="aspect-square w-full" />
                  </li>
                ))
              : portfolio.map((img) => {
                  const liked = likedIds.includes(img.id);
                  return (
                    <li
                      key={img.id}
                      className={[
                        "group relative aspect-square overflow-hidden rounded-lg border bg-white transition",
                        liked
                          ? "border-[#C8A24B] ring-2 ring-[#C8A24B]/40"
                          : "border-[#EAE3D2]",
                      ].join(" ")}
                    >
                      <img
                        src={img.thumbnail_url || img.url}
                        alt={img.title || "Portfolio sample"}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                      <button
                        type="button"
                        data-testid={`inspo-like-${img.id}`}
                        aria-label={liked ? "Unlike" : "Like"}
                        aria-pressed={liked}
                        onClick={() => toggleLike(img.id)}
                        className={[
                          "absolute right-2 top-2 rounded-full p-2 transition",
                          liked
                            ? "bg-[#C8A24B] text-[#1A1A1A]"
                            : "bg-white/80 text-[#1A1A1A]/55 opacity-0 group-hover:opacity-100 focus:opacity-100",
                        ].join(" ")}
                      >
                        <Heart
                          className={["h-4 w-4", liked ? "fill-current" : ""].join(" ")}
                        />
                      </button>
                    </li>
                  );
                })}
          </ul>
          {portfolio.length < total ? (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                data-testid="inspo-load-more"
                onClick={() => setOffset(offset + limit)}
                className="rounded-full border border-[#1A1A1A] px-5 py-2.5 text-xs uppercase tracking-[0.22em] text-[#1A1A1A] transition hover:bg-[#1A1A1A] hover:text-[#FAF7F2]"
              >
                Load more
              </button>
            </div>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function step4IsValid() {
  return true; // optional step
}
